import React, { useState } from 'react';

interface Props {
  currentUser: string;
  onClose: () => void;
}

type Category = 'Bug' | 'Praise/Idea';

export default function FeedbackModal({ currentUser, onClose }: Props): React.JSX.Element {
  const [category, setCategory] = useState<Category>('Bug');
  const [message,  setMessage]  = useState('');
  const [status,   setStatus]   = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errText,  setErrText]  = useState('');

  function submit(): void {
    const trimmed = message.trim();
    if (!trimmed) return;
    setStatus('sending');

    const kind    = category === 'Bug' ? 'Bug Report' : 'Praise / Idea';
    const subject = `[Agoracle Arena] ${kind} from @${currentUser}`;
    const mailto  =
      'mailto:feedback@agoracle.io' +
      `?subject=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(trimmed)}`;

    try {
      window.location.href = mailto;
      setStatus('sent');
    } catch (e) {
      setErrText((e as Error).message);
      setStatus('error');
    }
  }

  return (
    /* Backdrop */
    <div
      onClick={onClose}
      style={{
        position:        'fixed',
        inset:           0,
        backgroundColor: 'rgba(2,6,23,0.80)',
        backdropFilter:  'blur(3px)',
        zIndex:          1000,
        display:         'flex',
        alignItems:      'center',
        justifyContent:  'center',
        padding:         16,
      }}
    >
      {/* Panel — stop propagation so clicking inside doesn't close */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: '#0f172a',
          border:          '1px solid #334155',
          borderTop:       '2px solid #6366f1',
          borderRadius:    10,
          padding:         '22px 24px',
          width:           '100%',
          maxWidth:        420,
          fontFamily:      'monospace',
          boxShadow:       '0 0 40px rgba(99,102,241,0.2)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <p style={{ color: '#a5b4fc', fontSize: 11, letterSpacing: '0.18em', margin: 0 }}>
              SEND FEEDBACK
            </p>
            <p style={{ color: '#334155', fontSize: 9, letterSpacing: '0.12em', margin: '2px 0 0' }}>
              AS {currentUser.toUpperCase()}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', color: '#475569',
              fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: '0 4px',
            }}
          >
            ✕
          </button>
        </div>

        {status === 'sent' ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ color: '#86efac', fontSize: 13, letterSpacing: '0.08em', margin: 0 }}>
              ✓ Mail client opened.
            </p>
            <p style={{ color: '#64748b', fontSize: 10, letterSpacing: '0.08em', margin: '6px 0 0' }}>
              Send the draft to finish — addressed to feedback@agoracle.io.
            </p>
            <button
              onClick={onClose}
              style={{
                marginTop:       16,
                padding:         '6px 20px',
                borderRadius:    6,
                border:          '1px solid #334155',
                backgroundColor: '#1e293b',
                color:           '#94a3b8',
                fontSize:        10,
                cursor:          'pointer',
                fontFamily:      'monospace',
                letterSpacing:   '0.1em',
              }}
            >
              CLOSE
            </button>
          </div>
        ) : (
          <>
            {/* Category */}
            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={{ color: '#64748b', fontSize: 9, letterSpacing: '0.16em', display: 'block', marginBottom: 5 }}>
                CATEGORY
              </span>
              <select
                value={category}
                onChange={e => setCategory(e.target.value as Category)}
                style={{
                  width:           '100%',
                  backgroundColor: '#1e293b',
                  border:          '1px solid #334155',
                  borderRadius:    6,
                  color:           '#e2e8f0',
                  fontSize:        11,
                  padding:         '7px 10px',
                  fontFamily:      'monospace',
                  outline:         'none',
                  cursor:          'pointer',
                }}
              >
                <option value="Bug">🐛  Bug Report</option>
                <option value="Praise/Idea">💡  Praise / Idea</option>
              </select>
            </label>

            {/* Message */}
            <label style={{ display: 'block', marginBottom: 16 }}>
              <span style={{ color: '#64748b', fontSize: 9, letterSpacing: '0.16em', display: 'block', marginBottom: 5 }}>
                MESSAGE
              </span>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Describe the bug or share your idea..."
                rows={4}
                style={{
                  width:           '100%',
                  backgroundColor: '#1e293b',
                  border:          '1px solid #334155',
                  borderRadius:    6,
                  color:           '#e2e8f0',
                  fontSize:        11,
                  padding:         '8px 10px',
                  fontFamily:      'monospace',
                  resize:          'vertical',
                  outline:         'none',
                  boxSizing:       'border-box',
                }}
              />
            </label>

            {status === 'error' && (
              <p style={{ color: 'rgba(239,68,68,0.8)', fontSize: 10, margin: '0 0 10px' }}>
                {errText}
              </p>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={onClose}
                style={{
                  padding:         '7px 16px',
                  borderRadius:    6,
                  border:          '1px solid #334155',
                  backgroundColor: 'transparent',
                  color:           '#64748b',
                  fontSize:        10,
                  cursor:          'pointer',
                  fontFamily:      'monospace',
                  letterSpacing:   '0.1em',
                }}
              >
                CANCEL
              </button>
              <button
                onClick={submit}
                disabled={!message.trim() || status === 'sending'}
                style={{
                  padding:         '7px 20px',
                  borderRadius:    6,
                  border:          '1px solid #4f46e5',
                  backgroundColor: message.trim() && status !== 'sending' ? '#4f46e5' : '#1e1b4b',
                  color:           message.trim() && status !== 'sending' ? '#e0e7ff' : '#3730a3',
                  fontSize:        10,
                  cursor:          message.trim() && status !== 'sending' ? 'pointer' : 'not-allowed',
                  fontFamily:      'monospace',
                  letterSpacing:   '0.1em',
                  transition:      'background-color 0.15s',
                }}
              >
                {status === 'sending' ? 'SENDING…' : 'SEND'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
